import { FilterSpecification, type Map as MaplibreMap } from "maplibre-gl";
import type { FeatureCollection, Geometry } from "geojson";

import { Timezone } from "@/constants/dateTime";
import { SUBLAYERS } from "@/constants/map";
import { YEAR_MONTH_PATTERN } from "@/constants/regex";
import {
  DataSchemaType,
  Dataset,
  DatasetAttributeSchema,
  DatasetTimeseriesData,
  TimeseriesFormat,
  TimeseriesType
} from "@/types/dataset";
import { LayerType } from "@/types/map";
import { getTimeseriesFormatFromLabel } from "@/utils/datasetAttributes";
import { hasSeconds, parseWithDayjs } from "@/utils/date";
import { LayerSublayers, NON_POI_LAYER_TYPES } from "@/utils/map";
import { dedupePoiFeatures, getPointSourceBaseData } from "@/utils/map/point";

import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import localeData from "dayjs/plugin/localeData";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);
dayjs.extend(localeData);

const orderDays = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday"
];

/** Months since year 0, so a year-month series orders by year before month:
 * "2023-12" precedes "2024-01". */
const toMonthIndex = (yearMonth: string): number => {
  const [year, month] = yearMonth.split("-");
  return Number(year) * 12 + Number(month);
};

const toNumber = (value: string) =>
  value.trim() === "" ? Number.NaN : Number(value);

/** Time values that look numeric are stored as numbers in feature properties. */
const parseTimeValue = (value: string): string | number => {
  const number = toNumber(value);
  return Number.isNaN(number) ? value : number;
};

/** Groups rows by their `name` value and dedupes each group by POI ID. */
const groupFeaturesByTime = (
  features: FeatureCollection<Geometry>["features"],
  name: string
) => {
  const byTime = new Map<unknown, FeatureCollection<Geometry>["features"]>();
  for (const feature of features) {
    const time = feature.properties?.[name];
    const frame = byTime.get(time);
    if (frame) frame.push(feature);
    else byTime.set(time, [feature]);
  }
  for (const [time, frame] of byTime) {
    byTime.set(time, dedupePoiFeatures(frame));
  }
  return byTime;
};

/**
 * Returns one time frame's points (deduped by POI ID) to set on the source.
 * Returns undefined when the source already shows that frame, so the caller must apply the result.
 */
export const getPointSourceDataForTime = (
  map: MaplibreMap,
  datasetId: string,
  name: string,
  value: string
): FeatureCollection<Geometry> | undefined => {
  const baseData = getPointSourceBaseData(map, datasetId);
  if (!baseData || !value || baseData.time === value) return;
  baseData.time = value;

  // Group once so each frame change is a lookup, not a scan of every row
  if (baseData.frames?.attribute !== name) {
    baseData.frames = {
      attribute: name,
      byTime: groupFeaturesByTime(baseData.features, name)
    };
  }
  const features = baseData.frames.byTime.get(parseTimeValue(value)) ?? [];
  return { type: "FeatureCollection", features };
};

export const handleFilterFeaturesGroupByAttribute = (
  map: MaplibreMap | null,
  name: string,
  value: string,
  layerId: string,
  options?: {
    // Optional legend-filter expression to AND with the time filter so a
    // time-scrub doesn't wipe the user's legend hide/show selection. When
    // omitted, only the time `==` filter is applied (legacy behavior).
    extraFilter?: unknown;
    // When provided, the composed filter is applied to every data-driven
    // sublayer of the dataset (fill/outline/label/rank/stroke) — for parity
    // with handleFilterLayerByLegend. POINT datasets are skipped; use
    // getPointSourceDataForTime. When omitted, the filter is applied to the
    // first layer whose id starts with `layerId` (legacy behavior).
    dataset?: Dataset;
  }
) => {
  if (!map || !value) return;

  const style = map.getStyle();

  if (!style) return;

  const timeFilter: unknown = ["==", ["get", name], parseTimeValue(value)];

  const composedFilter = options?.extraFilter
    ? ["all", options.extraFilter, timeFilter]
    : timeFilter;

  // Points are clustered on the source, so a layer filter can't reach them
  if (options?.dataset?.properties.spatialType === LayerType.POINT) return;

  if (options?.dataset) {
    const spatialType = options.dataset.properties.spatialType;
    const sublayers: LayerSublayers = SUBLAYERS(options.dataset.data.id)[
      spatialType
    ];
    NON_POI_LAYER_TYPES.forEach((layerKey) => {
      const sublayerId = sublayers?.[layerKey];
      // Guard with getLayer: not every sublayer in SUBLAYERS is necessarily
      // present in the live style (e.g. a sublayer that wasn't rendered yet),
      // and setFilter throws on a non-existing layer.
      if (sublayerId && map.getLayer(sublayerId)) {
        map.setFilter(sublayerId, composedFilter as FilterSpecification);
      }
    });
    return;
  }

  const matchLayerId = style.layers.find((layer) =>
    layer.id.startsWith(layerId)
  );

  if (!matchLayerId) return;

  map.setFilter(matchLayerId.id, composedFilter as FilterSpecification);
};

export const getTypeTimeSeries = (
  categories: DatasetAttributeSchema[]
): TimeseriesType => {
  const groupByCategories = categories.find((cat) => cat.group_by_attr);

  if (!groupByCategories) return TimeseriesType.TIME; // default to time if no groupByCategories found

  const category = groupByCategories;

  if (category?.data_type === DataSchemaType.TIME) {
    return TimeseriesType.TIME;
  } else if (category?.data_type === DataSchemaType.TEXT) {
    return TimeseriesType.DAY;
  } else if (category?.data_type === DataSchemaType.INT) {
    return TimeseriesType.WEEK;
  } else if (category?.data_type === DataSchemaType.DATE) {
    return TimeseriesType.DATE;
  } else {
    return TimeseriesType.TIME; // default to time if type is unknown
  }
};

export const sortTimeSeriesData = (
  data: DatasetTimeseriesData[],
  type: TimeseriesType,
  timezone?: Timezone
): DatasetTimeseriesData[] => {
  if (type === TimeseriesType.DAY) {
    // `?? ""` covers the empty input the callers' contract still allows.
    if (YEAR_MONTH_PATTERN.test(data[0]?.time ?? "")) {
      return [...data].sort(
        (a, b) => toMonthIndex(a.time) - toMonthIndex(b.time)
      );
    }
    return [...data].sort(
      (a, b) => orderDays.indexOf(a.time) - orderDays.indexOf(b.time)
    );
  } else if (type === TimeseriesType.WEEK) {
    return [...data].sort((a, b) => Number(a.time) - Number(b.time));
  } else if (type === TimeseriesType.DATE) {
    return [...data].sort(
      (a, b) => dayjs(a.time).valueOf() - dayjs(b.time).valueOf()
    );
  } else {
    return [...data].sort((a, b) => {
      const hourA = dayjs.utc(a.time).tz(timezone?.code).hour();
      const hourB = dayjs.utc(b.time).tz(timezone?.code).hour();

      return hourA - hourB;
    });
  }
};

const toMonth = (value: string) => {
  const lower = value.toLowerCase();
  const shortMonths = dayjs.monthsShort();
  const index = dayjs
    .months()
    .findIndex(
      (month, i) =>
        month.toLowerCase() === lower || shortMonths[i].toLowerCase() === lower
    );
  return index === -1 ? toNumber(value) : index + 1;
};

const toDayName = (value: string) => {
  const lower = value.toLowerCase();
  const index = orderDays.findIndex(
    (day) =>
      day.toLowerCase() === lower || day.slice(0, 3).toLowerCase() === lower
  );
  return index === -1 ? Number.NaN : index;
};

// "14" and "14:00" both read as minutes since midnight.
const toMinutesOfDay = (value: string) => {
  const [hour, minute = "0"] = value.split(":");
  return toNumber(hour) * 60 + toNumber(minute);
};

/** Reads "<year><sep><part>" as one ascending number, e.g. "2026-Q1" with 4 parts a year. */
const toYearAndPart =
  (pattern: RegExp, partsPerYear: number) => (value: string) => {
    const match = pattern.exec(value);
    if (!match) return Number.NaN;
    const part = Number(match[2]);
    if (part < 1 || part > partsPerYear) return Number.NaN;
    return Number(match[1]) * partsPerYear + part;
  };

// "23/09/2026" or "09-23-2026": the day and month can come in either order.
const DAY_MONTH_DATE_PATTERN = /^(\d{2})[-/](\d{2})[-/]\d{4}/;
const TIME_ONLY_PATTERN = /^\d{1,2}:\d{2}/;

/** True when the series' slash or dash dates put the day first. A part above
 * 12 tells the order; when no value has one, the day comes first. */
export const isDayFirstSeries = (times: string[]): boolean => {
  for (const time of times) {
    const match = DAY_MONTH_DATE_PATTERN.exec(time.trim());
    if (!match) continue;
    if (Number(match[1]) > 12) return true;
    if (Number(match[2]) > 12) return false;
  }
  return true;
};

const getDayMonthFormats = (isDayFirst: boolean) =>
  ["/", "-"].flatMap((separator) => {
    const date = isDayFirst
      ? `DD${separator}MM${separator}YYYY`
      : `MM${separator}DD${separator}YYYY`;
    return [date, `${date} HH:mm`, `${date} HH:mm:ss`];
  });

/** Reads a date or date-time as UTC, so it is shown as written. */
const parseTimeseriesDate = (value: string, isDayFirst: boolean) => {
  const trimmed = value.trim();
  // The shared parser also reads a time alone, which is not a date here.
  if (TIME_ONLY_PATTERN.test(trimmed)) return null;
  if (!DAY_MONTH_DATE_PATTERN.test(trimmed)) return parseWithDayjs(trimmed);
  for (const format of getDayMonthFormats(isDayFirst)) {
    const parsed = dayjs.utc(trimmed, format, true);
    if (parsed.isValid()) return parsed;
  }
  return null;
};

const toTimestamp = (value: string, isDayFirst: boolean) =>
  parseTimeseriesDate(value, isDayFirst)?.valueOf() ?? Number.NaN;

const FORMAT_RANK: Record<
  Exclude<
    TimeseriesFormat,
    TimeseriesFormat.OTHER | TimeseriesFormat.DATE | TimeseriesFormat.DATETIME
  >,
  (value: string) => number
> = {
  [TimeseriesFormat.YEAR]: toNumber,
  [TimeseriesFormat.YEAR_MONTH]: (value) =>
    YEAR_MONTH_PATTERN.test(value) ? toMonthIndex(value) : Number.NaN,
  [TimeseriesFormat.YEAR_MONTH_DAY]: (value) =>
    dayjs.utc(value, "YYYY-MM-DD", true).valueOf(),
  [TimeseriesFormat.YEAR_QUARTER]: toYearAndPart(/^(\d{4})-Q(\d)$/i, 4),
  [TimeseriesFormat.YEAR_WEEK]: toYearAndPart(/^(\d{4})-W(\d{2})$/i, 53),
  [TimeseriesFormat.QUARTER]: (value) =>
    /^Q[1-4]$/i.test(value) ? Number(value.slice(1)) : Number.NaN,
  [TimeseriesFormat.MONTH]: toMonth,
  [TimeseriesFormat.WEEK_OF_YEAR]: (value) =>
    toNumber(value.replace(/^W/i, "")),
  [TimeseriesFormat.DAY_OF_MONTH]: toNumber,
  [TimeseriesFormat.DAY_NAME]: toDayName,
  [TimeseriesFormat.HOUR]: toMinutesOfDay,
  [TimeseriesFormat.MINUTE]: toNumber
};

/** Time format the user picked for the group-by attribute. A time column is
 * left out so it keeps the legacy path, which shows hours in the user's timezone. */
export const getTimeseriesFormat = (
  categories: DatasetAttributeSchema[]
): TimeseriesFormat | undefined => {
  const groupBy = categories.find((category) => category.group_by_attr);
  if (!groupBy || groupBy.data_type === DataSchemaType.TIME) return undefined;
  return getTimeseriesFormatFromLabel(groupBy.data_label);
};

/** Orders points by the picked time format; values it cannot read go last. */
export const sortTimeSeriesByFormat = (
  data: DatasetTimeseriesData[],
  format: TimeseriesFormat
): DatasetTimeseriesData[] => {
  if (format === TimeseriesFormat.OTHER) {
    const isAllNumeric = data.every((d) => !Number.isNaN(toNumber(d.time)));
    return [...data].sort((a, b) =>
      isAllNumeric
        ? Number(a.time) - Number(b.time)
        : a.time.localeCompare(b.time)
    );
  }

  const isDayFirst = isDayFirstSeries(data.map((d) => d.time));
  const rank =
    format === TimeseriesFormat.DATE || format === TimeseriesFormat.DATETIME
      ? (value: string) => toTimestamp(value, isDayFirst)
      : FORMAT_RANK[format];
  return [...data].sort((a, b) => {
    const rankA = rank(a.time);
    const rankB = rank(b.time);
    if (Number.isNaN(rankA)) return Number.isNaN(rankB) ? 0 : 1;
    if (Number.isNaN(rankB)) return -1;
    return rankA - rankB;
  });
};

export const formatTimeLabel = (
  time: string,
  format: TimeseriesFormat,
  isDayFirst: boolean
): string => {
  if (format !== TimeseriesFormat.DATE && format !== TimeseriesFormat.DATETIME)
    return time;
  const parsed = parseTimeseriesDate(time, isDayFirst);
  if (!parsed) return time;
  if (format === TimeseriesFormat.DATE) return parsed.format("DD/MM/YYYY");
  // Seconds show only when the value carries them.
  return parsed.format(
    hasSeconds(time) ? "DD/MM/YYYY HH:mm:ss" : "DD/MM/YYYY HH:mm"
  );
};

/** Sorts with the format picked in Data Settings when there is one, otherwise the legacy way. */
export const sortTimeSeriesForChart = ({
  data,
  categories,
  type,
  timezone,
  isFormatEnabled
}: {
  data: DatasetTimeseriesData[];
  categories: DatasetAttributeSchema[];
  type: TimeseriesType;
  timezone?: Timezone;
  isFormatEnabled: boolean;
}) => {
  const timeFormat = isFormatEnabled
    ? getTimeseriesFormat(categories)
    : undefined;
  const sortedData = timeFormat
    ? sortTimeSeriesByFormat(data, timeFormat)
    : sortTimeSeriesData(data, type, timezone);
  const isDayFirst = isDayFirstSeries(data.map((d) => d.time));
  return { timeFormat, sortedData, isDayFirst };
};
